import { createSelector } from '@reduxjs/toolkit';
import type { SerializedEditorState } from 'lexical';

import { LanguageEnum } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { TranscriptTranslations } from 'js/src/data/transcript/transcript-types';
import {
    $getSilenceSpans,
    $getUntrimmedFillerWords,
} from 'js/src/data/transcript/transcript-utils';
import { getTranslatedEditors } from 'js/src/data/translated-editors';
import { type DeepReadonly, UNKNOWN_LANGUAGE_CODE } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function isTranscriptStateAvailable(state: RootState) {
    return !!state.transcript;
}

export function isTranscriptVisible(state: RootState) {
    return state.transcript.transcript_visible;
}

export function isSceneConversionModalVisible(state: RootState) {
    return state.transcript.scene_conversion_modal_visible;
}

export function getTranscriptState(state: RootState) {
    return state.undoable.present.transcript;
}

export function getTranscripts(state: RootState) {
    return getTranscriptState(state).transcripts;
}

export function getTranscriptById(state: RootState, sceneGroupId: string) {
    return new PlainJsStoreORM(getTranscripts(state)).getById(sceneGroupId);
}

export function getTranscriptTranslationMap(state: RootState, sceneGroupId: string) {
    return getTranscriptById(state, sceneGroupId)?.translations;
}

/**
 * Get the translation language for a transcript specified by the scene group id.
 * For now, we only support translating the transcript into a single other language.
 * @param state redux state
 * @param sceneGroupId the id of the scene group for which we want the translation data
 * @returns either the language code (eg. "en", "fe", "de", etc) or undefined if the transcript has not been translated
 */
export function getTranscriptTranslationLanguage(
    state: RootState,
    sceneGroupId: string,
): LanguageEnum | undefined {
    const translations = getTranscriptTranslationMap(state, sceneGroupId);
    if (!translations) {
        return undefined;
    }
    return (Object.keys(translations) as LanguageEnum[])[0];
}

/**
 * Get the editorState for the language that was translated
 * @param state redux state
 * @param sceneGroupId the id of the scene group for which we want the translation data
 * @returns the editorState of the translated transcript
 */
export function getTranscriptTranslationById(state: RootState, sceneGroupId: string) {
    const translations = getTranscriptTranslationMap(state, sceneGroupId);
    const languageCode = getTranscriptTranslationLanguage(state, sceneGroupId);
    if (!translations || !languageCode) {
        return undefined;
    }
    return translations[languageCode]?.editorState;
}

export function sceneGroupHasTranscriptTranslation(state: RootState, sceneGroupId: string) {
    return !!getTranscriptTranslationLanguage(state, sceneGroupId);
}

export function shouldDisableOverlays(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    if (transcript) {
        return transcript.disableOverlays;
    }
    return false;
}

export function getIsOriginalTranscript(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    if (transcript) {
        return transcript.isOriginal;
    }
    return false;
}

export function getIsOriginalOrMissingTranscript(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    if (!transcript) {
        return true;
    }
    return transcript.isOriginal;
}

export function getOriginalTranscriptLanguage(state: RootState, sceneGroupId: string) {
    const transcript = getTranscriptById(state, sceneGroupId);
    const language = transcript?.language;
    if (!language) {
        return UNKNOWN_LANGUAGE_CODE;
    }
    return language;
}

export function getTranscriptIds(state: RootState): readonly string[] {
    return getTranscripts(state).allIds;
}

export const getAllTranscriptEditorStates = createSelector(
    (state: RootState) => getTranscripts(state),
    (transcripts) => {
        const rawTranscripts: Record<string, DeepReadonly<SerializedEditorState>> = {};
        new PlainJsStoreORM(transcripts).forEach((transcript) => {
            rawTranscripts[transcript.sceneGroupId] = transcript.editorState;
        });
        return rawTranscripts;
    },
);

export function getTranscriptsToSave(state: RootState) {
    const transcripts: Record<
        string,
        {
            disableOverlays: boolean;
            editorState: DeepReadonly<SerializedEditorState>;
            translations: DeepReadonly<TranscriptTranslations> | undefined;
            language: string;
        }
    > = {};
    new PlainJsStoreORM(getTranscripts(state)).forEach((transcript) => {
        transcripts[transcript.sceneGroupId] = {
            disableOverlays: !!transcript.disableOverlays,
            editorState: transcript.editorState,
            translations: transcript.translations,
            language: transcript.language ?? LanguageEnum.XX,
        };
    });
    return transcripts;
}

export function getTranscriptCurrentTime(state: RootState) {
    return state.transcript.current_time;
}

export function getTranscriptCurrentTimeRelativeToSceneGroup(state: RootState) {
    return getTranscriptCurrentTime(state).currentTimeMillis;
}

export const getLexicalEditors = (state: RootState) => {
    return state.transcript.lexical_editors;
};

export const getEditorFromSceneGroupId = (state: RootState, sceneGroupId: string) => {
    return new PlainJsStoreORM(getLexicalEditors(state)).getById(sceneGroupId)?.editor;
};

export const getLexicalControlsItsOwnState = (
    state: RootState,
    sceneGroupId: string,
    langCode?: LanguageEnum | null,
) => {
    if (!langCode) {
        // we are dealing with the main transcript. Look up the lexicalControlsItsOwnState
        // value in that part of the redux store
        const lexicalControlsItsOwnState = new PlainJsStoreORM(getLexicalEditors(state)).getById(
            sceneGroupId,
        )?.lexicalControlsItsOwnState;
        return !!lexicalControlsItsOwnState;
    } else {
        // we are dealing with a translated transcript. Look up the lexicalControlsItsOwnState
        // value in that part of the redux store
        const translatedEditorsData = new PlainJsStoreORM(getTranslatedEditors(state)).getById(
            sceneGroupId,
        );
        const lexicalControlsItsOwnState =
            translatedEditorsData?.translations[langCode]?.lexicalControlsItsOwnState;
        return !!lexicalControlsItsOwnState;
    }
};

export function getTimingMode(state: RootState) {
    return state.transcript.timing_mode;
}

export function isTimingModeActive(state: RootState) {
    return getTimingMode(state).assetId !== null;
}

export function getTimingModeAssetId(state: RootState) {
    return getTimingMode(state).assetId;
}

export function getTimingModeSceneId(state: RootState) {
    return getTimingMode(state).sceneId;
}

export function getTimingModeSceneGroupId(state: RootState) {
    return getTimingMode(state).sceneGroupId;
}

export function countRemovableFillerWords(state: RootState, sceneGroupId: string): number {
    const editor = getEditorFromSceneGroupId(state, sceneGroupId);
    if (!editor) {
        return 0;
    }
    return editor.getEditorState().read(() => $getUntrimmedFillerWords().length);
}

export function hasRemovableFillerWords(state: RootState, sceneGroupId: string): boolean {
    return countRemovableFillerWords(state, sceneGroupId) > 0;
}

export function hasRemovableSilences(
    state: RootState,
    sceneGroupId: string,
    minDuration: number,
): boolean {
    const editor = getEditorFromSceneGroupId(state, sceneGroupId);
    if (!editor) {
        return false;
    }
    let result = false;
    editor.getEditorState().read(() => {
        result = $getSilenceSpans().some(
            ([start, end]) => end.endTime - start.startTime >= minDuration,
        );
    });
    return result;
}
